<template>
  <v-dialog :value="dialog" persistent max-width="720px" :fullscreen="$vuetify.breakpoint.xsOnly">
    <v-card>
      <v-form @submit.prevent>
        <v-card-title>
          <span class="headline">{{ item ? "Editar novedad" : "Nueva novedad" }}</span>
        </v-card-title>

        <v-card-text>
          <div class="row">
            <div class="col-md-12 pb-0">
              <BaseInput label="Título*" name="title" v-model="form.title" rules="required|max:255" :validationErrors="errors" />
            </div>
          </div>

          <div class="row">
            <div class="col-md-12 py-0">
              <div class="text-left mb-3">
                <div class="d-flex align-items-center mb-2">
                  <font-awesome-icon class="mr-2 icon-chevron" icon="chevron-right" />
                  <v-label class="label-input">Mensaje</v-label>
                </div>
                <v-textarea name="body" v-model="form.body" rows="3" outlined dense></v-textarea>
              </div>
            </div>
          </div>

          <div class="row">
            <div class="col-md-7 py-0">
              <BaseInput label="Link o video de YouTube" name="link_url" v-model="form.link_url" :rules="link_rules" :validationErrors="errors" />
            </div>
            <div class="col-md-5 py-0">
              <BaseInput label="Texto del link" name="link_text" v-model="form.link_text" rules="max:255" :validationErrors="errors" />
            </div>
          </div>

          <div class="news-modal__files">
            <div class="d-flex align-items-center mb-2">
              <font-awesome-icon class="mr-2 icon-chevron" icon="chevron-right" />
              <v-label class="label-input">Archivos</v-label>
            </div>
            <div class="container-documents">
              <div class="drop-area news-modal__drop" role="button" tabindex="0" @click="$refs.file_input.click()" @keydown.enter.prevent="$refs.file_input.click()" @dragover.prevent @dragenter.prevent @drop.prevent="addFiles(Array.from($event.dataTransfer.files))">
                <font-awesome-icon class="download-icon" icon="fa-download" />
                <p class="mt-3 mb-0">Suelta los archivos aquí o haz clic para seleccionarlos</p>
                <input ref="file_input" type="file" hidden multiple :accept="accept" @change="onPickFiles" />
              </div>
            </div>
            <div class="news-modal__hint">PDF, imágenes o videos. Hasta {{ max_files }} archivos y 38 MB por guardado.</div>
            <span v-if="files_error" class="red--text">{{ files_error }}</span>
          </div>

          <div v-for="attachment in current_attachments" :key="'current-' + attachment.id" class="news-modal__file">
            <span class="news-modal__file-name">{{ attachment.name }}</span>
            <span class="news-modal__file-size">{{ formatSize(attachment.size) }}</span>
            <v-btn icon small aria-label="Quitar archivo" title="Quitar archivo" @click="removeCurrent(attachment)"><v-icon small color="red">mdi-close</v-icon></v-btn>
          </div>

          <div v-for="(entry, index) in new_files" :key="entry.key" class="news-modal__file">
            <span class="news-modal__file-name">{{ entry.file.name }}</span>
            <span class="news-modal__file-size">{{ formatSize(entry.file.size) }}</span>
            <v-btn icon small aria-label="Quitar archivo" title="Quitar archivo" @click="removeNew(index)"><v-icon small color="red">mdi-close</v-icon></v-btn>
          </div>

          <v-checkbox v-model="form.pinned" label="Fijar arriba del muro"></v-checkbox>
        </v-card-text>

        <v-card-actions class="news-modal__actions">
          <v-spacer></v-spacer>
          <v-btn color="error" text :disabled="saving" @click="$emit('close')">Cerrar</v-btn>
          <v-btn text :disabled="saving" @click="save(false)">{{ is_published ? "Pasar a borrador" : "Guardar borrador" }}</v-btn>
          <v-btn color="success" text :loading="saving" @click="save(true)">{{ is_published ? "Guardar" : "Publicar" }}</v-btn>
        </v-card-actions>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script>
import BaseInput from "@/components/BaseInput.vue";
import { newsService } from "@/libs/ws/newsService";
import { MAX_FILES, MAX_UPLOAD_BYTES, VALID_EXTENSIONS, getExtension, formatSize } from "./newsModel";

export default {
  name: "ModalNews",
  components: { BaseInput },
  props: {
    dialog: Boolean,
    // La novedad a editar; null para crear
    item: Object,
  },
  data() {
    return {
      form: this.emptyForm(),
      current_attachments: [],
      removed_ids: [],
      new_files: [],
      next_file_key: 0,
      link_rules: "regex:^https?://\\S+$|max:1000",
      files_error: "",
      saving: false,
      max_files: MAX_FILES,
      accept: VALID_EXTENSIONS.map((extension) => "." + extension).join(","),
    };
  },
  computed: {
    is_published() {
      return this.item != null && this.item.enable;
    },
  },
  watch: {
    dialog(open) {
      if (open) this.reset();
    },
  },
  methods: {
    formatSize,
    emptyForm() {
      return { title: "", body: "", link_url: "", link_text: "", pinned: false };
    },
    reset() {
      this.form = this.item
        ? {
            title: this.item.title,
            body: this.item.body || "",
            link_url: this.item.link_url || "",
            link_text: this.item.link_text || "",
            pinned: this.item.pinned,
          }
        : this.emptyForm();
      this.current_attachments = this.item ? this.item.attachments.slice() : [];
      this.removed_ids = [];
      this.new_files = [];
      this.files_error = "";
      this.$validator.reset();
    },
    onPickFiles(event) {
      this.addFiles(Array.from(event.target.files));
      // Permite volver a elegir el mismo archivo despues de quitarlo
      event.target.value = "";
    },
    addFiles(files) {
      this.files_error = "";

      const invalid = files.find((file) => !VALID_EXTENSIONS.includes(getExtension(file.name)));
      if (invalid) {
        this.files_error = "El tipo de archivo de " + invalid.name + " no está permitido.";
        return;
      }
      files.forEach((file) => this.new_files.push({ key: this.next_file_key++, file }));
      this.validateFiles();
    },
    removeNew(index) {
      this.new_files.splice(index, 1);
      this.validateFiles();
    },
    removeCurrent(attachment) {
      this.removed_ids.push(attachment.id);
      this.current_attachments = this.current_attachments.filter((current) => current.id != attachment.id);
      this.validateFiles();
    },
    validateFiles() {
      const total = this.new_files.reduce((sum, entry) => sum + entry.file.size, 0);
      if (this.current_attachments.length + this.new_files.length > MAX_FILES) {
        this.files_error = "Una novedad puede tener hasta " + MAX_FILES + " archivos.";
      } else if (total > MAX_UPLOAD_BYTES) {
        this.files_error = "Los archivos nuevos superan los 38 MB por guardado.";
      } else {
        this.files_error = "";
      }
      return this.files_error == "";
    },
    buildFormData(enable) {
      const data = new FormData();
      if (this.item) data.append("id", this.item.id);
      data.append("title", this.form.title);
      data.append("body", this.form.body);
      data.append("link_url", this.form.link_url);
      data.append("link_text", this.form.link_text);
      data.append("pinned", this.form.pinned);
      data.append("enable", enable);

      this.new_files.forEach((entry) => data.append("files[]", entry.file));

      if (this.item) {
        data.append("remove_attachment_ids", JSON.stringify(this.removed_ids));
      }
      return data;
    },
    async save(enable) {
      if (this.saving) return;
      const valid = await this.$validator.validate();
      if (!valid || !this.validateFiles()) return;

      this.saving = true;
      const data = this.buildFormData(enable);
      (this.item ? newsService.update(data) : newsService.create(data))
        .then((response) => {
          if (response.status) {
            this.$toast.success(response.msg, "OK");
            this.$emit("saved");
          } else {
            this.$toast.error(response.msg, "Error");
          }
        })
        .catch(() => this.$toast.error("No se pudo guardar la novedad.", "Error"))
        .finally(() => (this.saving = false));
    },
  },
};
</script>

<style scoped>
.news-modal__drop {
  max-width: none;
  padding: 20px;
}
.news-modal__drop .download-icon {
  font-size: 40px;
}
.news-modal__hint {
  margin-top: 6px;
  font-size: 12px;
  color: #64748b;
}
.news-modal__file {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  font-size: 14px;
}
.news-modal__file-name {
  flex: 1;
  min-width: 0;
  color: #0f172a;
  overflow-wrap: anywhere;
}
.news-modal__file-size {
  flex-shrink: 0;
  font-size: 12px;
  color: #64748b;
}
.news-modal__actions {
  flex-wrap: wrap;
}
</style>
